import { useImageUrl } from '@/hooks/useFile'
import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Circle, Group, Image as KonvaImage, Layer, Line, Stage, Text } from 'react-konva'
import useImage from 'use-image'
import { MapStageProps } from '../types/mapStateProps'
import { Shape } from '../types/shape'

const toPixelPoints = (points: [number, number][], w: number, h: number) => {
  return points.flatMap(([x, y]) => [x * w, y * h])
}

const getCenter = (points: [number, number][]) => {
  const xs = points.map(p => p[0])
  const ys = points.map(p => p[1])

  const centerX = xs.reduce((a, b) => a + b, 0) / xs.length
  const centerY = ys.reduce((a, b) => a + b, 0) / ys.length

  return [centerX, centerY]
}

function getTextOffset(text: string, fontSize: number) {
  const approxWidth = text.length * (fontSize * 0.4)
  return approxWidth / 2
}

function getBoundingBox(points: [number, number][]) {
  const xs = points.map(p => p[0])
  const ys = points.map(p => p[1])

  return {
    minX: Math.min(...xs),
    maxX: Math.max(...xs),
    minY: Math.min(...ys),
    maxY: Math.max(...ys),
  }
}

export function MapStage(props: MapStageProps) {
  const { t } = useTranslation()
  const width = props.width
  const height = props.height
  const { data: imageUrl } = useImageUrl(props.imageId)

  const [image] = useImage(imageUrl ?? '')
  const [displayImage, setDisplayImage] = useState<HTMLImageElement | undefined>(undefined)
  const [isImageLoading, setIsImageLoading] = useState(false)
  const loadingStartRef = useRef<number | null>(null)
  const [stableShapes, setStableShapes] = useState<Shape[]>([])

  useEffect(() => {
    if (!imageUrl) {
      setDisplayImage(undefined)
      setIsImageLoading(false)
      return
    }

    setIsImageLoading(true)
    setDisplayImage(undefined)
    loadingStartRef.current = Date.now()
  }, [imageUrl])

  useEffect(() => {
    if (!image) return

    const MIN_DURATION = 300

    const elapsed = Date.now() - (loadingStartRef.current ?? 0)
    const remaining = Math.max(0, MIN_DURATION - elapsed)

    setTimeout(() => {
      setDisplayImage(image)
      setIsImageLoading(false)
    }, remaining)
  }, [image])

  useEffect(() => {
    if (!isImageLoading) {
      setStableShapes(props.shapes ?? [])
    }
  }, [isImageLoading, props.shapes])

  const hasNoMap = !props.imageId
  const isMapLoading = props.isLoading || (!hasNoMap && (!imageUrl || !displayImage || isImageLoading))

  useEffect(() => {
    props.onMapLoadingChange?.(isMapLoading)
  }, [isMapLoading, props.onMapLoadingChange])

  const canInteractShape =
    (props.allowShapeInteraction ?? true) && (!!props.onSelect || !!props.onHover) && props.mode === 'view'
  const canInteractDot =
    (props.allowDotInteraction ?? true) && (!!props.onSelect || !!props.onHover) && props.mode === 'view'

  return (
    <div data-testid="map-stage" className="flex w-full relative">
      {(hasNoMap || isMapLoading) && (
        <div className="absolute inset-0 flex items-center justify-center bg-white/50 pointer-events-none">
          <span className="text-xs text-muted-foreground">
            {isMapLoading ? t('common.map.loading') : t('common.map.noMap')}
          </span>
        </div>
      )}

      <Stage
        className="flex w-full justify-center overflow-auto"
        width={width}
        height={height}
        onClick={props.onMapClick}
      >
        <Layer>
          {displayImage && (
            <>
              {/* Map image */}
              <KonvaImage image={displayImage} width={width} height={height} listening={false} />

              {/* Drawing line */}
              {props.mode === 'draw' && props.drawType === 'dot' && props.point && (
                <Circle x={props.point[0] * width} y={props.point[1] * height} radius={5} fill="green" />
              )}

              {props.mode === 'draw' && props.drawType === 'shape' && (
                <Line
                  points={props.points?.map((p, i) => (i % 2 === 0 ? p * width : p * height))}
                  stroke="blue"
                  strokeWidth={2}
                />
              )}

              {props.paths?.map((p, i) => (
                <Line key={i} points={p} stroke="red" strokeWidth={3} dash={[8, 4]} />
              ))}

              {stableShapes.map(shape => {
                const isSelected = props.selectedId === shape.id
                const isHovered = props.hoveredId === shape.id

                if (shape.points) {
                  const [cx, cy] = getCenter(shape.points)
                  const selection = {
                    entity: 'shape' as const,
                    entityType: shape.entityType,
                    id: shape.id,
                  }
                  const box = getBoundingBox(shape.points)
                  const textWidth = (box.maxX - box.minX) * width

                  const handleSelect = () => {
                    if (!props.onSelect) return

                    props.onSelect?.(isSelected ? null : selection)
                  }
                  return (
                    <Group
                      key={shape.id}
                      onClick={canInteractShape ? handleSelect : undefined}
                      onTap={canInteractShape ? handleSelect : undefined}
                      onMouseEnter={
                        canInteractShape
                          ? () => {
                              props.onHover?.(selection)
                              document.body.style.cursor = 'pointer'
                            }
                          : undefined
                      }
                      onMouseLeave={
                        canInteractShape
                          ? () => {
                              props.onHover?.(null)
                              document.body.style.cursor = 'default'
                            }
                          : undefined
                      }
                    >
                      <Line
                        points={toPixelPoints(shape.points, width, height)}
                        closed
                        fill={shape.color}
                        opacity={isSelected ? 0.8 : isHovered ? 0.7 : 0.5}
                        stroke={isSelected ? 'black' : shape.color}
                        strokeWidth={isSelected ? 2 : 1}
                      />
                      <Text
                        text={shape.label}
                        x={box.minX * width}
                        y={cy * height - 6}
                        width={textWidth}
                        align="center"
                        fontSize={12}
                      />
                    </Group>
                  )
                }

                return null
              })}

              {props.dots?.map(dot => {
                const isSelected = props.selectedId === dot.id
                const isHovered = props.hoveredId === dot.id

                const [x, y] = dot.point
                const selection = {
                  entity: 'dot' as const,
                  dotType: dot.dotType,
                  id: dot.id,
                }

                const handleSelect = () => {
                  if (!props.onSelect) return

                  props.onSelect?.(isSelected ? null : selection)
                }
                return (
                  <React.Fragment key={dot.id}>
                    <Circle
                      listening={canInteractDot}
                      x={x * width}
                      y={y * height}
                      radius={isSelected ? 7 : 5}
                      fill={dot.color}
                      opacity={isSelected ? 0.9 : isHovered ? 0.8 : 0.6}
                      onClick={canInteractDot ? handleSelect : undefined}
                      onTap={canInteractDot ? handleSelect : undefined}
                      onMouseEnter={
                        canInteractDot
                          ? () => {
                              props.onHover?.(selection)
                              document.body.style.cursor = 'pointer'
                            }
                          : undefined
                      }
                      onMouseLeave={
                        canInteractDot
                          ? () => {
                              props.onHover?.(null)
                              document.body.style.cursor = 'default'
                            }
                          : undefined
                      }
                    />

                    <Text
                      text={dot.label}
                      x={x * width + 8}
                      y={y * height - 8}
                      fontSize={10}
                      fill="black"
                      listening={false}
                    />
                  </React.Fragment>
                )
              })}
            </>
          )}
        </Layer>
      </Stage>
    </div>
  )
}
